import { useEffect, useState } from 'react';
import { Checkbox } from 'components/ui/checkbox';
import { useAudit } from './audit-context';
import { Card } from 'components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Badge } from 'components/ui/badge';
import { getLatestAudit } from './PreviousAuditCard';
import { getStatusColor, getStatusIcon } from '../InternalAuditOverview/components/AuditMatrix';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import groupBy from 'lodash.groupby';
import { Button } from 'components/ui/button';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { cn } from 'lib/utils';

export function TaskSelection({ tasksFilter, orgId }: { tasksFilter?: string[]; orgId: string }) {
  const { audits, selectedTasks, masterTaskList, setSelectedTasks } = useAudit();

  const audit = getLatestAudit(audits, orgId);

  const handleTaskToggle = (taskId: string) => {
    setSelectedTasks(
      selectedTasks.includes(taskId)
        ? selectedTasks.filter((id) => id !== taskId)
        : [...selectedTasks, taskId]
    );
  };

  const groupedMasterList = groupBy(masterTaskList, 'category');

  function getHasDeviation(tasks: any[]) {
    if (!audit) return false;

    for (const task of tasks) {
      const hasSome = Object.entries(audit?.subTasksMap).some(
        //@ts-ignore
        ([_, value]) => task.id === value.taskId && value.status === 'deviation'
      );
      if (hasSome) {
        return true;
      }
    }
    return false;
  }
  function getHasObservations(tasks: any[]) {
    if (!audit) return false;

    for (const task of tasks) {
      const hasSome = Object.entries(audit?.subTasksMap).some(
        //@ts-ignore
        ([_, value]) => task.id === value.taskId && value.status === 'observation'
      );
      if (hasSome) {
        return true;
      }
    }
    return false;
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="space-y-1">
        {Object.entries(groupedMasterList)
          .sort((a, b) => a[0].localeCompare(b[0], 'da-DK', { sensitivity: 'base' }))
          .map(([category, tasks]) => {
            if (tasks.filter((t) => !tasksFilter?.includes(t.id))?.length < 1) return null;
            const hasDeviationPreviously = getHasDeviation(
              tasks.filter((t) => t.category === category)
            );

            const hasObservationPreviously = getHasObservations(
              tasks.filter((t) => t.category === category)
            );

            return (
              <CollapseGroup
                category={category}
                key={category}
                tasks={tasks.filter((t) => !tasksFilter?.includes(t.id))}
                hasDeviationsPreviously={hasDeviationPreviously}
                hasObservationPreviously={hasObservationPreviously}
              >
                {tasks
                  .filter((t) => !tasksFilter?.includes(t.id))
                  .sort((a, b) => a.label.localeCompare(b.label, 'da-DK', { sensitivity: 'base' }))
                  .map((task, index) => {
                    const hasDeviationPreviously =
                      audit &&
                      Object.entries(audit?.subTasksMap).some(
                        //@ts-ignore
                        ([_, value]) => task.id === value.taskId && value.status === 'deviation'
                      );

                    const hasObservationPreviously =
                      audit &&
                      Object.entries(audit?.subTasksMap).some(
                        //@ts-ignore
                        ([_, value]) => task.id === value.taskId && value.status === 'observation'
                      );

                    return (
                      <Card
                        key={task.id}
                        className="flex w-[500px] px-4 mt-2 items-center space-x-2 shadow-none"
                      >
                        <Accordion type="multiple" className="w-full">
                          <AccordionItem
                            className="border-none"
                            value={`item-${index}`}
                            key={index}
                          >
                            <div className="flex items-center justify-between ">
                              <div className="flex w-full items-center space-x-2">
                                <Checkbox
                                  id={task.id}
                                  checked={selectedTasks.includes(task.id)}
                                  onCheckedChange={() => handleTaskToggle(task.id)}
                                />
                                <label
                                  htmlFor={task.id}
                                  className="flex grow text-sm cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {task.label}
                                </label>
                                {hasDeviationPreviously && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Badge className={getStatusColor('deviation')}>
                                          {getStatusIcon('deviation')}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="text-sm">
                                        Seneste audit havde afvigelser på denne opgave
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {hasObservationPreviously && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Badge className={getStatusColor('observation')}>
                                          {getStatusIcon('observation')}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="text-sm">
                                        Seneste audit havde observationer på denne opgave
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {/* <div className="" /> */}
                                {/* <Badge variant="secondary"> */}
                                <div className="text-xs text-muted-foreground">
                                  {task.subTasks?.length} punkt{task.subTasks?.length !== 1 && 'er'}
                                </div>
                                {/* </Badge> */}
                              </div>
                              <AccordionTrigger></AccordionTrigger>
                            </div>
                            <AccordionContent>
                              <ul>
                                {task.subTasks.map((subTask) => (
                                  <li className="flex items-center space-x-2">
                                    <p className="text-sm">{subTask.label}</p>
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </Card>
                    );
                  })}
              </CollapseGroup>
            );
          })}
      </div>
    </div>
  );
}

function CollapseGroup({
  category,
  tasks,
  hasDeviationsPreviously,
  hasObservationPreviously,
  children,
}: {
  category: string;
  tasks: { id: string; label: string; subTasks: { id: string; label: string }[] }[];
  hasDeviationsPreviously: boolean;
  hasObservationPreviously: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { selectedTasks, setSelectedTasks } = useAudit();
  const isAllChecked = tasks.every((task) => selectedTasks.includes(task.id));
  const isSomeChecked = tasks.some((task) => selectedTasks.includes(task.id));

  const groupCheckedState = isAllChecked ? true : isSomeChecked ? 'indeterminate' : false;

  const handleGroupTaskToggle = (
    groupCheckedState: boolean | 'indeterminate',
    taskIds: string[]
  ) => {
    setSelectedTasks(
      groupCheckedState === true
        ? selectedTasks.filter((id) => !taskIds.includes(id))
        : Array.from(new Set([...selectedTasks, ...taskIds]))
    );
  };
  return (
    <div key={category} className="flex flex-col bg-slate-50 py-2 px-4 rounded-md">
      <div className="flex items-center space-x-2 justify-between">
        <div className="flex items-center space-x-2">
          {/* <Tooltip>
                    <TooltipTrigger asChild> */}
          <Checkbox
            id={category}
            checked={groupCheckedState}
            // checked={selectedTasks.includes(task.id)}
            onCheckedChange={() =>
              handleGroupTaskToggle(
                groupCheckedState,
                tasks.map((task) => task.id)
              )
            }
          />
          {/* </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-sm">Vælg alle opgaver i denne kategori. </div>
                    </TooltipContent>
                  </Tooltip> */}
          <Badge variant="secondary">{category}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {hasDeviationsPreviously && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Badge className={getStatusColor('deviation')}>
                    {getStatusIcon('deviation')}
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-sm">Seneste audit havde afvigelser på denne opgave</div>
              </TooltipContent>
            </Tooltip>
          )}
          {hasObservationPreviously && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Badge className={getStatusColor('observation')}>
                    {getStatusIcon('observation')}
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-sm">Seneste audit havde observationer på denne opgave</div>
              </TooltipContent>
            </Tooltip>
          )}
          <Button size="icon" variant="ghost" onClick={() => setOpen(!open)}>
            {open ? <ChevronsDownUp className="h-4" /> : <ChevronsUpDown className="h-4" />}
          </Button>
        </div>
      </div>
      <div
        className={cn(
          'grid overflow-hidden transition-all duration-300 ease-in-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden space-y-1">{children}</div>
      </div>
    </div>
  );
}
